import { types, flow, getEnv } from 'mobx-state-tree';

import Ad from '../models/banner/Ad';
import Error from '../models/Error';
import StatefulStore from '../models/StatefulStore';
import TextBanner from '../models/banner/TextBanner';
import RequestState, { RequestStateType } from '../types/RequestState';
import { createErrorModel } from '../util/error';
import { paramsToQueryIdentifier } from '../util/query';
import BannerPages from '../models/banner/BannerPages';

const AdStore = StatefulStore.named('AdStore')
  .props({
    adQueries: types.optional(types.map(types.array(Ad)), {}),
    adQueryStates: types.optional(types.map(RequestStateType), {}),
    adQueryError: types.maybeNull(Error),
    bannerZones: types.maybeNull(BannerPages),
    globalBanners: types.optional(types.array(Ad), []),
    textBanners: types.optional(types.array(TextBanner), []),
  })
  .actions((self) => {
    return {
      setup: (banners) => {
        self.setBanners(banners);
      },
      setBanners: (banners) => {
        if (banners) {
          self.bannerZones = banners.bannerZones;
          self.globalBanners = banners.globalBanners;
          self.textBanners = banners.textBanners;
        }
      },
      loadAds: flow(function* loadAds(searchParameters, path = '') {
        const queryIdentifier = paramsToQueryIdentifier(searchParameters);

        if (self.adQueries.get(queryIdentifier)) {
          return;
        }
        if (self.adQueryStates.get(queryIdentifier) === RequestState.LOADING) {
          return;
        }

        self.setLoading(true);
        self.adQueryStates.set(queryIdentifier, RequestState.LOADING);

        try {
          const ads = yield getEnv(self).apiWrapper.request(
            `banners${path}`,
            { params: searchParameters },
            { active_section: null }
          );
          self.adQueries.set(queryIdentifier, ads);
        } catch (error) {
          self.setError(error);
          self.adQueryError = createErrorModel(error);
          self.adQueryStates.set(queryIdentifier, RequestState.ERROR);
          throw error;
        }

        self.setLoading(false);
        self.adQueryStates.set(queryIdentifier, RequestState.LOADED);
      }),
    };
  })
  .views((self) => {
    const zoneIds = (pageKey) =>
      (self.bannerZones?.[pageKey]?.zones || []).map((zone) => zone.id);

    const findZone = (pageKey, zoneId) =>
      (self.bannerZones?.[pageKey]?.zones || []).find(
        (zone) => zone.id === zoneId
      );

    return {
      shouldShowZone(pageKey, zoneId, isMobile) {
        const zone = findZone(pageKey, zoneId);
        if (!zone) {
          return false;
        }
        return isMobile ? zone.has_mobile : zone.has_non_mobile;
      },
      getZoneMeta(pageKey, zoneId) {
        return findZone(pageKey, zoneId) || null;
      },
      get campaignProductsAds() {
        return zoneIds('CAMPAIGN_PRODUCTS');
      },
      get categoryPageAds() {
        return zoneIds('CATEGORY_PAGE');
      },
      get checkoutPageAds() {
        return [];
        //return self.bannerZones?.CHECKOUT_PAGE.zones.slice() || [];
      },
      get customerProductsAds() {
        return zoneIds('CUSTOMER_PRODUCTS');
      },
      get frontPageAds() {
        return {
          zones: zoneIds('FRONT_PAGE'),
        };
      },
      get manufacturerPageAds() {
        return zoneIds('MANUFACTURER_PAGE');
      },
      get newProductsAds() {
        return zoneIds('NEW_PRODUCTS');
      },
      get popularProductsAds() {
        return zoneIds('POPULAR_PRODUCTS');
      },
      get productPageAds() {
        return zoneIds('PRODUCT_PAGE');
      },
      get recommendedProductsAds() {
        return zoneIds('RECOMMENDED_PRODUCTS');
      },
      get sectionPageAds() {
        return {
          zones: zoneIds('SECTION_FRONT_PAGE'),
        };
      },
      get soldProductsAds() {
        return zoneIds('SOLD_PRODUCTS');
      },
      getGlobalAdsByAdZone(adZone) {
        const ads = self.globalBanners;
        let adsByZone = null;
        if (ads) {
          adsByZone = ads.filter((ad) => {
            return ad.zones.indexOf(adZone) !== -1;
          });
        }
        return adsByZone;
      },
      getAdsByAdZone(searchParameters, adZone) {
        const queryIdentifier = paramsToQueryIdentifier(searchParameters);
        const ads = self.adQueries.get(queryIdentifier);
        let adsByZone = null;
        if (ads) {
          adsByZone = ads.filter((ad) => {
            return ad.zones.indexOf(adZone) !== -1;
          });
        }
        return adsByZone;
      },
      getAdsBySearchParameters(searchParameters) {
        const queryIdentifier = paramsToQueryIdentifier(searchParameters);
        return self.adQueries.get(queryIdentifier);
      },
      getAdQueryStateBySearchParameters(searchParameters) {
        const queryIdentifier = paramsToQueryIdentifier(searchParameters);
        return self.adQueryStates.get(queryIdentifier);
      },
    };
  });

export default AdStore;
